import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { TeamMember } from 'hooks/useGetTeammember'
import React, { useEffect, useMemo } from 'react'
import { MentionsInput, Mention, MentionsInputStyle } from 'react-mentions'
import Avatar from 'appAssets/appImages/Avatar.png'

interface MentionTextAreaProps {
  value: string
  placeholder: string
  onChange: (e: string, newPlainTextValue: string, mentions: any[]) => void
  teamMembers: TeamMember[]
  textAreaRef?: React.RefObject<HTMLTextAreaElement>
  onSubmit?: () => void
}

// Extended team member interface for mentions
interface ExtendedTeamMember extends TeamMember {
  email: string
  display: string
  isCoachLogic?: boolean
}

const defaultStyle: MentionsInputStyle = {
  control: {
    fontSize: 16,
    fontWeight: 'normal',
    border: 0,
  },
  input: {
    overflow: 'auto',
    height: 70,
    border: '1px solid transparent',
    outline: 0,
  },
  highlighter: {
    boxSizing: 'border-box',
    overflow: 'hidden',
    height: 70,
  },
  suggestions: {
    list: {
      backgroundColor: 'white',
      border: '1px solid #E5E7EB',
      fontSize: 14,
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      position: 'absolute',
      bottom: '100%',
      marginBottom: '8px',
      left: 0,
      right: 0,
      zIndex: 1000,
      minWidth: '320px',
      maxHeight: '280px',
      overflowY: 'auto',
      padding: 0,
    },
    item: {
      padding: 0,
      borderBottom: 'none',
      '&focused': {
        backgroundColor: '#F9FAFB',
      },
    },
  },
}

const mentionStyle = {
  backgroundColor: '#EAE9FE',
  borderRadius: '4px',
}

const MentionTextArea: React.FC<MentionTextAreaProps> = ({
  value,
  placeholder,
  onChange,
  teamMembers,
  textAreaRef,
  onSubmit,
}) => {
  // Add event listener for keyboard shortcuts
  useEffect(() => {
    const textareaElement = textAreaRef?.current
    if (!textareaElement) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && onSubmit) {
        e.preventDefault()
        onSubmit()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onChange('', '', [])
      } else if (e.key === 'Backspace' && e.ctrlKey) {
        e.preventDefault()
        const lastSpaceIndex = value.trimEnd().lastIndexOf(' ')
        onChange(
          lastSpaceIndex !== -1 ? value.substring(0, lastSpaceIndex + 1) : '',
          lastSpaceIndex !== -1 ? value.substring(0, lastSpaceIndex + 1) : '',
          []
        )
      }
    }

    textareaElement.addEventListener('keydown', handleKeyDown)
    return () => {
      textareaElement.removeEventListener('keydown', handleKeyDown)
    }
  }, [textAreaRef, value, onChange, onSubmit])

  // Get user's email or generate a display email
  const getUserEmail = (member: TeamMember): string => {
    // Generate a display email from name since TeamMember doesn't have email
    const firstName = member.firstName?.toLowerCase() || 'user'
    const lastName = member.lastName?.toLowerCase() || ''
    const domain = 'company.com' // Default domain

    if (lastName) {
      return `${firstName}.${lastName}@${domain}`
    }
    return `${firstName}@${domain}`
  }

  // Transform team members to extended format and add CoachLogic at the top
  const extendedTeamMembers: ExtendedTeamMember[] = useMemo(() => {
    // Add CoachLogic as the first option
    const coachLogic = {
      id: 'coachLogic',
      firstName: 'Coach',
      lastName: 'Logic',
      email: 'coachlogic@ai.assistant',
      display: 'Coach Logic',
      isCoachLogic: true,
    }

    // Map team members and add CoachLogic at the beginning
    const members = teamMembers.map((member) => ({
      ...member,
      email: getUserEmail(member),
      display: `${member.firstName} ${member.lastName}`.trim(),
      isCoachLogic: false,
    }))

    return [coachLogic, ...members]
  }, [teamMembers])

  return (
    <div className="w-full min-h-[75px] relative">
      <MentionsInput
        value={value}
        onChange={(e, newValue, newPlainTextValue, mentions) => {
          onChange(newValue, newPlainTextValue, mentions)
        }}
        placeholder={placeholder}
        style={defaultStyle}
        inputRef={textAreaRef}
        a11ySuggestionsListLabel={'Suggested mentions'}
      >
        <Mention
          trigger="@"
          data={extendedTeamMembers.map((member) => ({
            id: member.id,
            display: member.display,
            email: member.email,
            firstName: member.firstName,
            lastName: member.lastName,
            isCoachLogic: member.isCoachLogic,
          }))}
          renderSuggestion={(
            suggestion: any,
            search: string,
            highlightedDisplay: any,
            index: number,
            focused: boolean
          ) => {
            // Check if this is CoachLogic
            const isCoachLogic = suggestion.id === 'coachLogic'

            const initial = suggestion.display
              ?.split(' ')
              .map((name: string) => name[0])
              .join('')

            return (
              <div className="w-full">
                <div
                  className={`px-4 py-3 cursor-pointer transition-colors duration-150 ${
                    focused ? 'bg-gray-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <AppAvatar
                      size="sm"
                      type={isCoachLogic ? 'image' : 'mixed'}
                      shape="Rounded"
                      src={isCoachLogic ? Avatar : undefined}
                      text={isCoachLogic ? undefined : initial}
                    />
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-2">
                        {highlightedDisplay || suggestion.display}
                        {isCoachLogic && (
                          <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                            AI Assistant
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          }}
          markup='<mention id="__id__">@__display__</mention>'
          style={mentionStyle}
          displayTransform={(id, display) => `@${display}`}
        />
      </MentionsInput>
    </div>
  )
}

export default MentionTextArea
